import React, { useEffect, useRef, useState, useMemo } from 'react';
import '../styles/Lore.css';
import { useTranslation } from 'react-i18next';
// Importando iconos
import { FaSpaceShuttle, FaUniversity, FaGlobeAmericas, FaBook } from 'react-icons/fa';
import { GiCrossedSwords, GiAlienSkull, GiBirdMask, GiDiploma } from 'react-icons/gi';
import { MdExplore } from 'react-icons/md';

// Importar imágenes
import palarNestImage from '../assets/PalarNestPanoramico.webp';
import franklinYeungImage from '../assets/FranklinYeung.webp';
import tanaNovachekImage from '../assets/TanaNovachekPanoramico.webp';
import lore2Image from '../assets/Lore2.webp';
import gavilanImage from '../assets/gavilan.webp';
import nacimientoPalarNest from '../assets/nacimientopalarnest.webp';
import encuentroConFranklin from '../assets/encuentroconfranklin.webp';
import ataqueAFranklin from '../assets/ataqueafranklin.webp';
import elPrimerContrato from '../assets/elprimercontrato.webp';
import fundacionCorvusCorax from '../assets/fundaciondecorvuscorax.webp';
import primeraAsambleaCorvusNocta from '../assets/primeraasambleacorvusnocta.webp';
import laEmboscadaEnPyro from '../assets/laemboscadaenpyro.webp';
import encuentroConTanaNovachek from '../assets/encuentrocontananovachek.webp';
import elNuevoRumbo from '../assets/elnuevorumbo.webp';
import retiroDePalarNest from '../assets/retirodepalarnest.webp';
import liderazgoTanaNovachek from '../assets/liderazgodetananovachek.webp';

const Lore = () => {
    const { t, i18n } = useTranslation();
    const timelineRef = useRef(null);
    const headerRef = useRef(null); // Referencia para el header
    const [activeEvent, setActiveEvent] = useState(null);
    const [filter, setFilter] = useState('all'); // Estado para el filtro: 'all', 'corvus', 'sc'
    const [, forceUpdate] = useState();
    
    // Forzar re-render cuando cambie el idioma
    useEffect(() => {
        forceUpdate({});
    }, [i18n.language]);

    // Función para obtener la imagen correspondiente a cada evento
    const getEventImage = (eventKey) => {
        const imageMap = {
            'palar_birth': palarNestImage,
            'franklin_meeting': [palarNestImage, franklinYeungImage],
            'franklin_attack': franklinYeungImage,
            'first_contract': palarNestImage,
            'corvus_corax': palarNestImage,
            'tana_meeting': [palarNestImage, tanaNovachekImage],
            'pyro_ambush': palarNestImage,
            'new_direction': [palarNestImage, tanaNovachekImage],
            'first_assembly': [palarNestImage, tanaNovachekImage],
            'palar_retirement': palarNestImage,
            'palar_death': palarNestImage,
            'tana_leadership': tanaNovachekImage,
            'corvus_consolidation': tanaNovachekImage,
            'modern_corvus': lore2Image
        };
        return imageMap[eventKey] || null;
    };

    // Función para crear el array de eventos con las traducciones actuales
    const timelineEvents = useMemo(() => {
        // Obtener todos los eventos del archivo de traducción
        const events = Object.entries(t('lore.events', { returnObjects: true }))
            .map(([key, event]) => ({
                year: event.year,
                type: event.type,
                eventKey: `lore.events.${key}`,
                title: event.title,
                content: event.content,
                image: getEventImage(key)
            }))
            .sort((a, b) => a.year - b.year);

        // Crear array final con separadores de era
        const timeline = [];
        
        // Añadir era antigua
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.ancient.title'),
            description: t('lore.eras.ancient.description')
        });

        // Filtrar y añadir eventos antiguos (antes del año 0)
        events.filter(e => e.year < 0).forEach(e => timeline.push(e));

        // Añadir era de expansión
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.expansion.title'),
            description: t('lore.eras.expansion.description')
        });

        // Filtrar y añadir eventos de expansión (2000-2300)
        events.filter(e => e.year >= 2000 && e.year < 2300).forEach(e => timeline.push(e));

        // Añadir era Tevarin
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.tevarin.title'),
            description: t('lore.eras.tevarin.description')
        });

        // Filtrar y añadir eventos Tevarin (2300-2450)
        events.filter(e => e.year >= 2300 && e.year < 2450).forEach(e => timeline.push(e));

        // Añadir era Messer
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.messer.title'),
            description: t('lore.eras.messer.description')
        });

        // Filtrar y añadir eventos Messer (2450-2650)
        events.filter(e => e.year >= 2450 && e.year < 2650).forEach(e => timeline.push(e));

        // Añadir era de contactos alienígenas
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.alien_contacts.title'),
            description: t('lore.eras.alien_contacts.description')
        });

        // Filtrar y añadir eventos de contactos alienígenas (2650-2750)
        events.filter(e => e.year >= 2650 && e.year < 2750).forEach(e => timeline.push(e));

        // Añadir era de opresión Messer
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.messer_oppression.title'),
            description: t('lore.eras.messer_oppression.description')
        });

        // Filtrar y añadir eventos de opresión Messer (2750-2800)
        events.filter(e => e.year >= 2750 && e.year < 2800).forEach(e => timeline.push(e));

        // Añadir era de la nueva UEE
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.new_uee.title'),
            description: t('lore.eras.new_uee.description')
        });

        // Filtrar y añadir eventos de la nueva UEE (2800-2900)
        events.filter(e => e.year >= 2800 && e.year < 2900).forEach(e => timeline.push(e));

        // Añadir era moderna
        timeline.push({
            type: 'era-separator',
            era: t('lore.eras.modern.title'),
            description: t('lore.eras.modern.description')
        });

        // Filtrar y añadir eventos modernos (2900 en adelante)
        events.filter(e => e.year >= 2900).forEach(e => timeline.push(e));

        return timeline;
    }, [t, i18n.language]); // Dependencias del useMemo

    // Obtener los eventos filtrados
    const filteredEvents = useMemo(() => {
        if (filter === 'all') return timelineEvents;
        return timelineEvents.filter(event => 
            event.type === filter || event.type === 'era-separator'
        );
    }, [filter, timelineEvents]);
    
    // Memoizar los índices de los eventos especiales
    const specialEventIndices = useMemo(() => {
        const indices = {};
        timelineEvents.forEach((event, index) => {
            if (event.type === 'corvus') {
                switch (event.eventKey) {
                    case 'lore.events.palar_birth':
                        indices.birth = index;
                        break;
                    case 'lore.events.franklin_meeting':
                        indices.franklinMeeting = index;
                        break;
                    case 'lore.events.franklin_attack':
                        indices.franklinAttack = index;
                        break;
                    case 'lore.events.first_contract':
                        indices.firstContract = index;
                        break;
                    case 'lore.events.corvus_corax':
                        indices.corvusCorax = index;
                        break;
                    case 'lore.events.first_assembly':
                        indices.firstAssembly = index;
                        break;
                    case 'lore.events.pyro_ambush':
                        indices.pyroAmbush = index;
                        break;
                    case 'lore.events.tana_meeting':
                        indices.tanaMeeting = index;
                        break;
                    case 'lore.events.new_direction':
                        indices.newDirection = index;
                        break;
                    case 'lore.events.palar_retirement':
                        indices.palarRetirement = index;
                        break;
                    case 'lore.events.tana_leadership':
                        indices.tanaLeadership = index;
                        break;
                }
            }
        });
        return indices;
    }, [timelineEvents]);
    
    // Función para obtener el icono según el título o contenido del evento
    const getEventIcon = (event) => {
        // Si es un separador de era, no mostramos icono
        if (event.type === 'era-separator') return null;
        
        const title = event.title ? event.title.toLowerCase() : '';
        const year = event.year ? event.year : '';
        
        // Para los eventos de Corvus Nocta
        if (event.type === 'corvus') {
            if (title.includes('fundación') || title.includes('nacimiento')) {
                return <GiDiploma className="event-icon" />;
            } else if (title.includes('ataque') || title.includes('emboscada')) {
                return <GiCrossedSwords className="event-icon" />;
            } else if (title.includes('asamblea') || title.includes('encuentro')) {
                return <FaUniversity className="event-icon" />;
            } else {
                return <GiBirdMask className="event-icon" />; // Icono por defecto para Corvus
            }
        } 
        // Para los eventos de Star Citizen
        else if (event.type === 'sc') {
            if (title.includes('guerra') || title.includes('batalla') || title.includes('conflicto')) {
                return <GiCrossedSwords className="event-icon" />;
            } else if (title.includes('descubrimiento') || title.includes('exploración')) {
                return <MdExplore className="event-icon" />;
            } else if (title.includes('contacto') || title.includes('alien')) {
                return <GiAlienSkull className="event-icon" />;
            } else if (title.includes('colonización') || title.includes('asentamiento')) {
                return <FaGlobeAmericas className="event-icon" />;
            } else if (title.includes('fundación') || title.includes('creación') || title.includes('formación')) {
                return <GiDiploma className="event-icon" />;
            } else {
                return <FaSpaceShuttle className="event-icon" />; // Icono por defecto para SC
            }
        }
        return null;
    };
    
    // Función para navegar a eventos previos/siguientes
    const navigateBetweenEvents = (direction, onlyFiltered = false) => {
        const events = timelineEvents;
        
        // Obtener los eventos según el modo de navegación
        const filteredEvents = onlyFiltered ? 
            events.filter(e => e.type === 'corvus') : // Siempre filtrar por eventos de Corvus para los botones marrones
            events.filter(e => e.type !== 'era-separator'); // Para los botones azules, todos los eventos menos separadores
            
        if (filteredEvents.length === 0) return;
        
        // Si no hay evento activo o el evento activo no está en la lista filtrada
        if (activeEvent === null || !filteredEvents.find(e => events.indexOf(e) === parseInt(activeEvent))) {
            const eventIndex = direction === 'up' ? 
                events.indexOf(filteredEvents[filteredEvents.length - 1]) : 
                events.indexOf(filteredEvents[0]);
                
            setActiveEvent(eventIndex.toString());
            scrollToEvent(eventIndex);
            return;
        }
        
        // Obtener el índice actual en la lista de eventos filtrados
        const currentIndex = parseInt(activeEvent);
        const currentEventIndex = filteredEvents.findIndex(e => events.indexOf(e) === currentIndex);
        
        // Si el evento actual no está en la lista filtrada, ir al primero
        if (currentEventIndex === -1) {
            const eventIndex = events.indexOf(filteredEvents[0]);
            setActiveEvent(eventIndex.toString());
            scrollToEvent(eventIndex);
            return;
        }
        
        // Calcular el siguiente índice
        let nextEventIndex;
        if (direction === 'up') {
            nextEventIndex = currentEventIndex > 0 ? currentEventIndex - 1 : filteredEvents.length - 1;
        } else {
            nextEventIndex = currentEventIndex < filteredEvents.length - 1 ? currentEventIndex + 1 : 0;
        }
        
        // Obtener el evento y su índice en la lista completa
        const nextEvent = filteredEvents[nextEventIndex];
        const timelineIndex = events.indexOf(nextEvent);
        
        setActiveEvent(timelineIndex.toString());
        scrollToEvent(timelineIndex);
    };
    
    // Función para hacer scroll a un evento específico
    const scrollToEvent = (eventIndex) => {
        const eventElement = document.querySelector(`.timeline-event[data-index="${eventIndex}"]`);
        if (eventElement) {
            eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };
    
    // Función para activar manualmente un evento
    const handleEventClick = (index) => {
        setActiveEvent(index.toString());
    };

    // Función para mantener visible un detalle mientras el cursor está sobre él
    const handleDetailHover = (index, isEntering) => {
        if (isEntering) {
            setActiveEvent(index.toString());
        }
        // No hacer nada si el cursor sale - dejamos que el observer maneje esto
    };
    
    // Función para mantener los detalles visibles al hacer click en ellos
    const handleDetailClick = (index, event) => {
        // Prevenir la propagación para que no se cierre si se hace click fuera
        event.stopPropagation();
        // Mantener activo este detalle
        setActiveEvent(index.toString());
    };
    
    // Añadir un manejador para cerrar los detalles al hacer click fuera
    useEffect(() => {
        const handleOutsideClick = (event) => {
            // Verificar si el click fue dentro de un detalle o un punto
            const isInsidePoint = event.target.closest('.timeline-event-point');
            const isInsideDetail = event.target.closest('.event-detail');
            
            if (!isInsidePoint && !isInsideDetail && activeEvent !== null) {
                setActiveEvent(null);
            }
        };
        
        // Añadir el event listener
        document.addEventListener('click', handleOutsideClick);
        
        // Limpieza
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [activeEvent]);
    
    // Función para comprobar si un elemento está cerca del header
    const isElementNearHeader = (element) => {
        const headerHeight = document.querySelector('header') ? 
            document.querySelector('header').offsetHeight : 80;
        
        const rect = element.getBoundingClientRect();
        return rect.top <= headerHeight + 10; // 10px de margen adicional
    };

    // Configuración de la detección de Intersection Observer
    const setupScrollObserver = () => {
        // Obtener la altura del header
        const headerHeight = document.querySelector('header') ? 
            document.querySelector('header').offsetHeight : 80;
        
        const options = {
            root: null,
            rootMargin: `-${headerHeight}px 0px 150px 0px`,
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] // Más thresholds para detección más precisa
        };
        
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const eventId = entry.target.getAttribute('data-event-id');
                
                if (entry.isIntersecting) {
                    if (eventId) {
                        // Basta con que sea mínimamente visible para activarlo
                        if (entry.intersectionRatio >= 0.2) {
                            // Comprobar si está cerca del header
                            if (!isElementNearHeader(entry.target)) {
                                setActiveEvent(eventId);
                                entry.target.classList.add('active');
                            }
                        }
                    }
                } else {
                    // Verificar si el punto está cerca del borde superior (header)
                    const isNearHeader = isElementNearHeader(entry.target);
                    
                    // Solo desactivar si está cerca del header
                    if (isNearHeader) {
                        entry.target.classList.remove('active');
                        
                        // Si este era el evento activo, desactivarlo
                        if (activeEvent === eventId) {
                            setActiveEvent(null);
                        }
                    }
                }
            });
        }, options);
        
        // Observar solo los puntos de evento con un pequeño delay para asegurar que el DOM esté listo
        setTimeout(() => {
            document.querySelectorAll('.timeline-event-point').forEach(point => {
                observer.observe(point);
            });
        }, 100);
        
        return observer;
    };
    
    // Renderizar cada evento del timeline
    const renderTimelineEvents = () => {
        return filteredEvents.map((event, index) => {
            if (event.type === 'era-separator') {
                return (
                    <div key={`era-${index}`} className="era-separator">
                        <div className="era-marker"></div>
                        <div className="era-details">
                            <h2 className="era-title">{event.era}</h2>
                            <p className="era-description">{event.description}</p>
                        </div>
                    </div>
                );
            }

            // Evento regular de timeline
            const isOdd = index % 2 !== 0;
            const eventKey = event.eventKey || `event-${index}`;
            
            return (
                <div 
                    key={eventKey}
                    className="timeline-event" 
                    data-type={event.type}
                    data-index={index}
                >
                    <div 
                        className={`timeline-event-point ${activeEvent === index.toString() ? 'active' : ''}`}
                        data-event-id={index}
                        data-tooltip={t('lore.labels.clickToView')}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(index);
                        }}
                    >
                        {getEventIcon(event)}
                    </div>
                    
                    <div className={`event-year ${isOdd ? 'right-align' : 'left-align'}`}>
                        {event.year}
                    </div>
                    
                    <div 
                        className={`event-detail ${activeEvent === index.toString() ? 'active' : ''}`}
                        onMouseEnter={() => handleDetailHover(index, true)}
                        onMouseLeave={() => handleDetailHover(index, false)}
                        onClick={(e) => handleDetailClick(index, e)}
                    >
                        <div className="event-header">
                            <h3 className="event-title">
                                <span className="event-year-title">{event.year}</span>{' '}
                                {event.title}
                            </h3>
                            <span className="event-badge">
                                {event.type === 'corvus' 
                                    ? t('lore.labels.corvusNocta') 
                                    : t('lore.labels.starCitizen')}
                            </span>
                        </div>
                        
                        {event.image && (
                            <div className="event-image-container" 
                                data-name={event.title}
                            >
                                {Array.isArray(event.image) ? (
                                    <div className="multiple-images">
                                        {event.image.map((img, idx) => (
                                            <img 
                                                key={idx}
                                                src={img} 
                                                alt={`${event.title} - Personaje ${idx + 1}`} 
                                                className="event-image" 
                                                loading="eager" 
                                                fetchpriority="high"
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <img 
                                        src={event.image} 
                                        alt={event.title} 
                                        className="event-image" 
                                        loading="eager" 
                                        fetchpriority="high"
                                    />
                                )}
                            </div>
                        )}
                        
                        <div className="event-content">
                            <p>{event.content}</p>
                        </div>
                    </div>
                </div>
            );
        });
    };

    // Función para añadir texto curvado a las imágenes
    useEffect(() => {
        // Pre-renderizar los detalles para evitar retrasos en la carga
        const preloadEventDetails = () => {
            // Recorrer todos los eventos y "simular" un hover para que se precargen las traducciones
            timelineEvents.forEach((event, index) => {
                if (event.type !== 'era-separator' && event.eventKey) {
                    // Precargar las traducciones de títulos
                    if (event.eventKey) {
                        t(event.eventKey, { returnObjects: true });
                    }
                    
                    // Precargar las traducciones de contenido
                    if (event.content && event.content.startsWith('lore.')) {
                        const contentKey = event.content;
                        t(contentKey, { returnObjects: true });
                        
                        // También precargar las partes adicionales si existen
                        const contentBase = contentKey.substring(0, contentKey.lastIndexOf('.') + 1);
                        let i = 2;
                        while (i <= 5) { // Limitar a un máximo razonable
                            const additionalKey = `${contentBase}content${i}`;
                            t(additionalKey, { defaultValue: '' });
                            i++;
                        }
                    }
                }
            });
        };
        
        const addCurvedText = () => {
            const textContainers = document.querySelectorAll('.event-image-container');
            textContainers.forEach(container => {
                if (!container.getAttribute('data-name')) return;
                
                // Eliminar SVG previos si existen
                const oldSvg = container.querySelector('svg');
                if (oldSvg) {
                    oldSvg.remove();
                }
                
                // Crear SVG para texto curvo
                const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                
                // Crear un camino para el texto
                const textPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
                const containerWidth = 150; // Ancho del contenedor de la imagen
                
                // Calcular el radio para el path (un poco más grande que la imagen)
                const radius = containerWidth / 2 + 15;
                
                // Crear un arco para la parte inferior
                const pathData = `M 20,${radius * 1.2} A ${radius},${radius} 0 0,1 ${containerWidth - 20},${radius * 1.2}`;
                textPath.setAttribute("d", pathData);
                textPath.setAttribute("id", `textPath-${container.getAttribute('data-name').replace(/\s+/g, '-').toLowerCase()}`);
                textPath.setAttribute("fill", "none");
                svg.appendChild(textPath);
                
                // Crear el texto que seguirá el camino
                const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
                text.setAttribute("class", "curve-text");
                
                // Crear referencia al camino
                const textPathRef = document.createElementNS("http://www.w3.org/2000/svg", "textPath");
                textPathRef.setAttribute("href", `#textPath-${container.getAttribute('data-name').replace(/\s+/g, '-').toLowerCase()}`);
                textPathRef.setAttribute("startOffset", "50%");
                textPathRef.setAttribute("text-anchor", "middle");
                textPathRef.textContent = container.getAttribute('data-name');
                
                text.appendChild(textPathRef);
                svg.appendChild(text);
                
                // Añadir el SVG al contenedor
                container.appendChild(svg);
            });
        };
        
        // Establecer un evento activo por defecto (para que siempre haya algo visible)
        const setInitialActiveEvent = () => {
            // Primero buscar si ya hay un evento activo por URL o context
            if (activeEvent !== null) return;
            
            // Si no, buscar el primer evento que no sea un separador
            for (let i = 0; i < timelineEvents.length; i++) {
                if (timelineEvents[i].type !== 'era-separator') {
                    setActiveEvent(i.toString());
                    break;
                }
            }
        };
        
        // Ejecutar todo de manera organizada para optimizar el rendimiento
        const initTimeline = async () => {
            // Primero, precargar las traducciones y textos
            preloadEventDetails();
            
            // Pequeño delay para asegurar que el DOM esté listo (usar requestAnimationFrame es más eficiente)
            requestAnimationFrame(() => {
                // Luego, establecer el evento activo inicial
                setInitialActiveEvent();
                
                // Finalmente, añadir los textos curvos
                addCurvedText();
                
                // Configurar el observer después de todo lo demás
                const observer = setupScrollObserver();
                
                // Guardar el observer para limpieza
                window.timelineObserver = observer;
            });
        };
        
        // Iniciar el timeline
        initTimeline();
        
        // Limpiar al desmontar
        return () => {
            const svgs = document.querySelectorAll('.event-image-container svg');
            svgs.forEach(svg => svg.remove());
            if (window.timelineObserver) {
                window.timelineObserver.disconnect();
                delete window.timelineObserver;
            }
        };
    }, [t, timelineEvents, activeEvent]);

    return (
        <div className="lore-page" style={{ 
            backgroundColor: (
                activeEvent === specialEventIndices.birth?.toString() || 
                activeEvent === specialEventIndices.franklinMeeting?.toString() ||
                activeEvent === specialEventIndices.franklinAttack?.toString() ||
                activeEvent === specialEventIndices.firstContract?.toString() ||
                activeEvent === specialEventIndices.corvusCorax?.toString() ||
                activeEvent === specialEventIndices.firstAssembly?.toString() ||
                activeEvent === specialEventIndices.pyroAmbush?.toString() ||
                activeEvent === specialEventIndices.tanaMeeting?.toString() ||
                activeEvent === specialEventIndices.newDirection?.toString() ||
                activeEvent === specialEventIndices.palarRetirement?.toString() ||
                activeEvent === specialEventIndices.tanaLeadership?.toString()
            ) ? 'transparent' : 'var(--dark)' 
        }}>
            {activeEvent === specialEventIndices.birth?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${nacimientoPalarNest})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.franklinMeeting?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${encuentroConFranklin})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.franklinAttack?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${ataqueAFranklin})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.firstContract?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${elPrimerContrato})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.corvusCorax?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${fundacionCorvusCorax})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.firstAssembly?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${primeraAsambleaCorvusNocta})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.pyroAmbush?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${laEmboscadaEnPyro})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.tanaMeeting?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${encuentroConTanaNovachek})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.newDirection?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${elNuevoRumbo})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.palarRetirement?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${retiroDePalarNest})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {activeEvent === specialEventIndices.tanaLeadership?.toString() && (
                <div 
                    className="background-image"
                    style={{
                        backgroundImage: `url(${liderazgoTanaNovachek})`,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: -1,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.6,
                        pointerEvents: 'none'
                    }}
                />
            )}
            <div ref={headerRef} className="timeline-header-reference"></div>
            <div className="timeline-container">
                <h1 className="timeline-title">{t('lore.timelineTitle', 'Línea Temporal')}</h1>
                
                {/* Filtros de eventos */}
                <div className="timeline-filter">
                    <button 
                        className={`filter-button ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        {t('lore.filters.all', 'Todos')}
                    </button>
                    <button 
                        className={`filter-button ${filter === 'corvus' ? 'active' : ''}`}
                        onClick={() => setFilter('corvus')}
                    >
                        {t('lore.labels.corvusNocta', 'Corvus Nocta')}
                    </button>
                    <button 
                        className={`filter-button ${filter === 'sc' ? 'active' : ''}`}
                        data-filter="sc"
                        onClick={() => setFilter('sc')}
                    >
                        {t('lore.labels.starCitizen', 'Star Citizen')}
                    </button>
                </div>
                
                <div className="timeline-line" ref={timelineRef}>
                    {renderTimelineEvents()}
                </div>
                
                {/* Botones de navegación */}
                <div className="timeline-navigation">
                    {/* Navegación por todos los eventos */}
                    <div className="nav-buttons-group">
                        <div className="nav-buttons-container">
                            <button 
                                className="nav-button blue"
                                onClick={() => navigateBetweenEvents('up', false)}
                                aria-label={t('lore.navigation.prevAll', 'Evento anterior (todos)')}
                                title={t('lore.navigation.prevAll', 'Evento anterior (todos)')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
                                </svg>
                            </button>
                            <button 
                                className="nav-button blue"
                                onClick={() => navigateBetweenEvents('down', false)}
                                aria-label={t('lore.navigation.nextAll', 'Evento siguiente (todos)')}
                                title={t('lore.navigation.nextAll', 'Evento siguiente (todos)')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                                </svg>
                            </button>
                        </div>
                        <span className="nav-buttons-label blue">{t('lore.filters.all', 'Todos')}</span>
                    </div>

                    <div className="nav-buttons-separator"></div>

                    {/* Navegación por eventos de Corvus */}
                    <div className="nav-buttons-group">
                        <div className="nav-buttons-container">
                            <button 
                                className="nav-button"
                                onClick={() => navigateBetweenEvents('up', true)}
                                aria-label={t('lore.navigation.prevFiltered', 'Evento anterior (Corvus)')}
                                title={t('lore.navigation.prevFiltered', 'Evento anterior (Corvus)')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
                                </svg>
                            </button>
                            <button 
                                className="nav-button"
                                onClick={() => navigateBetweenEvents('down', true)}
                                aria-label={t('lore.navigation.nextFiltered', 'Evento siguiente (Corvus)')}
                                title={t('lore.navigation.nextFiltered', 'Evento siguiente (Corvus)')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                                </svg>
                            </button>
                        </div>
                        <span className="nav-buttons-label copper">{t('lore.labels.corvusNocta', 'Corvus Nocta')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Lore;